/**
 * Distance formatting utilities for fixed routes
 * Supports both metric (meters/kilometers) and imperial (feet/miles) units
 */

export type DistanceUnit = 'metric' | 'imperial';

const METERS_TO_FEET = 3.28084;
const METERS_TO_MILES = 0.000621371;
const FEET_TO_MILES = 5280;

/**
 * Convert meters to feet
 */
export function metersToFeet(meters: number): number {
  return meters * METERS_TO_FEET;
}

/**
 * Convert meters to miles
 */
export function metersToMiles(meters: number): number {
  return meters * METERS_TO_MILES;
}

/**
 * Format distance based on unit system
 * @param distanceMeters - Distance in meters
 * @param unit - Unit system ('metric' or 'imperial')
 * @returns Formatted string (e.g., "1.2km" or "0.7mi")
 */
export function formatDistance(distanceMeters: number | undefined, unit: DistanceUnit = 'metric'): string {
  if (distanceMeters === undefined || isNaN(distanceMeters)) {
    return '';
  }

  if (unit === 'imperial') {
    // Convert to miles
    const miles = metersToMiles(distanceMeters);
    if (miles >= 0.1) {
      // Show miles if >= 0.1 miles (about 160 meters)
      return `${miles.toFixed(1)}mi`;
    } else {
      // Show feet if < 0.1 miles
      const feet = metersToFeet(distanceMeters);
      return `${Math.round(feet)}ft`;
    }
  } else {
    // Metric system
    if (distanceMeters >= 1000) {
      // Show kilometers if >= 1000 meters
      return `${(distanceMeters / 1000).toFixed(1)}km`;
    } else {
      // Show meters if < 1000 meters
      return `${Math.round(distanceMeters)}m`;
    }
  }
}

